import { ComponentInterface } from '@fusioncharts/core/src/component-interface';
import { BLANKSTRING, BLANK, defined, COMMASTRING } from '@fusioncharts/core/src/lib';
/**
 * class to make the mathematical calculations for boxAndWhisker
 */
class BoxAndWhiskerStatisticalCalc extends ComponentInterface {
  /**
   * Function to set the data for the bw calculator
   * @param {string} val The comma separated string value
   */
  setArray (val) {
    var nf = this.getFromEnv('number-formatter'), // eslint-disable-line good-practices/no-single-usage-variable
      sum = 0, // eslint-disable-line good-practices/no-single-usage-variable
      len,
      value = val,
      dataArr;
    !value && (value = BLANK);
    // First we make an arry form the comma-separated value.
    // and then we sort and store the data array in dataArr
    //  for further calculation.
    dataArr = value.replace(/\s/g, BLANK).split(COMMASTRING);
    // Parse the values using NumberFormatter getCleanValue
    len = this.dataLength = dataArr && dataArr.length;

    while (len--) {
      sum += dataArr[len] = nf.getCleanValue(dataArr[len]);
    }

    // Now sort the data in ascending order
    // eslint-disable-next-line good-practices/no-function-dependency
    dataArr && dataArr.sort(function (a, b) {
      return a - b;
    });

    this.values = dataArr;
    // Calculate and store the Mean
    this.mean = sum / this.dataLength;
    this.getFrequencies();
  }

  /**
   * Function to get the values of quartiles
   * @return {Object} The value of 1st and 3rd quartile
   */
  getQuartiles () {
    var values = this.values,
      len = this.dataLength,
      isOdd = len % 2,
      q1Pos,
      q1LowPos,
      q3Pos,
      q3LowPos,
      q1Val,
      q3Val,
      method = BLANKSTRING; // eslint-disable-line good-practices/no-single-usage-variable

    switch (method) {
      case 'tukey':
        if (isOdd) {
          // Q1 = n + 3 / 4 And Q3 = 3N + 1 / 4
          q1Pos = (len + 3) / 4;
          q3Pos = ((len * 3) + 1) / 4;
        } else {
          // Q1 = n + 2 / 4 And Q3 = 3N + 2 / 4
          q1Pos = (len + 2) / 4;
          q3Pos = ((len * 3) + 2) / 4;
        }
        break;
      case 'mooremccabe':
        if (isOdd) {
          // Q1 = n + 1 / 4 And Q3 = 3N + 3 / 4
          q1Pos = (len + 1) / 4;
          q3Pos = q1Pos * 3;
        } else {
          // Q1 = n + 2 / 4 And Q3 = 3N + 2 / 4
          q1Pos = (len + 2) / 4;
          q3Pos = ((len * 3) + 2) / 4;
        }
        break;
      case 'freundperles':
        // Q1 = n + 3 / 4 And Q3 = 3N + 1 / 4
        q1Pos = (len + 3) / 4;
        q3Pos = ((len * 3) + 1) / 4;
        break;
      case 'mendenhallsincich':
        // Q1 = [n + 1 / 4] And [Q3 = 3N + 3 / 4]
        q1Pos = Math.round((len + 1) / 4);
        q3Pos = Math.round(q1Pos * 3);
        break;
      default:
        // Q1 = n + 1 / 4 And Q3 = 3N + 3 / 4
        q1Pos = (len + 1) / 4;
        q3Pos = q1Pos * 3;
        break;
    }

    q1Pos -= 1;
    q3Pos -= 1;
    q1LowPos = Math.floor(q1Pos);
    q3LowPos = Math.floor(q3Pos);

    q1Val = q1Pos - q1LowPos ? values[q1LowPos] + // eslint-disable-line good-practices/no-single-usage-variable
              ((values[Math.ceil(q1Pos)] - values[q1LowPos]) *
              (q1Pos - q1LowPos)) : values[q1Pos];
    q3Val = q3Pos - q3LowPos ? values[q3LowPos] + // eslint-disable-line good-practices/no-single-usage-variable
              ((values[Math.ceil(q3Pos)] - values[q3LowPos]) *
              (q3Pos - q3LowPos)) : values[q3Pos];

    this.quartiles = {
      q1: q1Val,
      q3: q3Val
    };
    return this.quartiles;
  }

  /**
   * Return min and max values from the data array.
   * @return {Object} Contains the minimum and maximum of the entered data
   */
  getMinMax () {
    var values = this.values;
    return {
      min: values[0],
      max: values[this.dataLength - 1]
    };
  }

  /**
   * calculate and returns the mean value
   * @return {number} mean value
   */
  getMean () {
    return this.mean;
  }

  /**
   * calculate the MeanDeviation
   * @return {number} meanDevaition
   */
  getMD () {
    var mean = this.mean, // eslint-disable-line good-practices/no-single-usage-variable
      freq = this.frequencies,
      freqLen = freq.length,
      freqObj,
      sum = 0; // eslint-disable-line good-practices/no-single-usage-variable

    while (freqLen--) {
      freqObj = freq[freqLen];
      sum += freqObj.frequency * Math.abs(freqObj.value - mean);
    }
    return sum / this.dataLength;
  }

  /**
   * calculate the standard deviation
   * @return {number} standard deviation
   */
  getSD () {
    var mean = this.mean, // eslint-disable-line good-practices/no-single-usage-variable
      values = this.values, // eslint-disable-line good-practices/no-single-usage-variable
      i = this.dataLength,
      len = i, // eslint-disable-line good-practices/no-single-usage-variable
      sum = 0; // eslint-disable-line good-practices/no-single-usage-variable

    while (i--) {
      sum += Math.pow(values[i] - mean, 2);
    }

    return Math.sqrt(sum / (len - 1));
  }

  /**
   * calculate the quartile deviation
   * @return {number} quartile deviation
   */
  getQD () {
    return (this.quartiles.q3 - this.quartiles.q1) * 0.5;
  }

  /**
   * calculate the frequencies and sum of the values
   */
  getFrequencies () {
    var frequenciesArr = [],
      len = this.dataLength, // eslint-disable-line good-practices/no-single-usage-variable
      values = this.values, // eslint-disable-line good-practices/no-single-usage-variable
      sum = 0, // eslint-disable-line good-practices/no-single-usage-variable
      value,
      freqObj,
      index;

    for (index = 0; index < len; index += 1) {
      sum += value = values[index];
      if (defined(frequenciesArr[index])) {
        frequenciesArr[index].frequency += 1;
      } else {
        freqObj = {};
        freqObj.value = value;
        freqObj.frequency = 1;
        frequenciesArr[index] = freqObj;
      }
    }
    this.sum = sum;
    this.frequencies = frequenciesArr;
  }

  /**
   * Function to get the median value
   * @return {number} median
   */
  getMedian () {
    var len = this.dataLength,
      midVal = len * 0.5,
      values = this.values;

    return len % 2 === 0 ? (values[midVal] + values[midVal - 1]) / 2
      : values[Math.floor(midVal)];
  }
}
export default BoxAndWhiskerStatisticalCalc;
